/**
 * 미션 화면(`/mission`, 시안 `_mission.html`) 설정값.
 *
 * 미션 **목록 자체는 실제 API** 를 쓴다 — `/api/campaign/items.json`
 * (`ControllerApiCampaign::items()`). 여기 있는 것은 화면 구성값뿐이다.
 */

import type { DaoCampaign } from '@/types/dao.ts';

/** 필터 탭 */
export interface MissionFilter {
  /** 시안의 `data-category` */
  id: string;
  label: string;
  /** `MC_CAMPAIGN.F_TYPE` 값. 이 탭에 묶을 미션을 고른다. */
  campaignTypes: string[];
  /** 목록 마크업 — 음악은 앨범 카드(`music-list`), 영상은 릴 카드(`reel-list`) */
  layout: 'music' | 'reel';
}

/**
 * **미션 형식 — M 음악 / V 영상.**
 *
 * 예전에는 `F_TYPE` 이 운영 방식(P 프로모션 / D 데일리)이라 시안의 탭
 * (음악형/영상형 = 콘텐츠 형식)과 축이 달랐다. 그 어긋남을 2026-09-10 에
 * 정리했다 — 마이그레이션 `20260910_mc_mission_type.sql`.
 *
 *   음악  올려 둔 mp3 를 듣는다. 파일 주소는 내려오지 않고, 인증된 요청으로
 *         바이트를 받아 Blob 으로 재생한다 (`src/components/audio-player.tsx`).
 *   영상  유튜브 영상을 본다 (`MC_CAMPAIGN.F_YOUTUBE_URL`).
 *
 * 시안에는 '게임형' · '참여형' 탭도 있지만 마크업이 주석 처리돼 있어 옮기지 않았다.
 */
export const MISSION_FILTERS: MissionFilter[] = [
  { id: 'music', label: '음악', campaignTypes: ['M'], layout: 'music' },
  { id: 'video', label: '영상', campaignTypes: ['V'], layout: 'reel' },
];

/** 목록이 비었을 때 (시안 `.mgrid-empty`) */
export const MISSION_EMPTY_TEXT = '아직 준비 중인 미션이에요.';

/**
 * 반복 주기 배지.
 *
 * 시안은 모든 항목에 '12시간마다' 를 고정으로 박아 뒀지만, 이제 미션마다 다르다 —
 * 관리 화면에서 정한 `F_INIT_TIME`(시간) 을 그대로 쓴다.
 *
 * `0` 은 '주기 없음' 이 아니라 **자정 기준 하루 한 번**이다. 그게 기본값이라
 * 대부분의 미션이 여기 해당한다.
 */
export function missionRepeat(campaign: DaoCampaign): string {
  const hours = campaign.initTime ?? 0;
  if (hours <= 0) return '하루 한 번';

  // 24의 배수는 '하루/이틀' 이 읽기 쉽다.
  if (hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? '하루마다' : `${days}일마다`;
  }
  return `${hours}시간마다`;
}

/** 추천 미션 배지 (시안 `.mission-badge`) */
export const MISSION_PICK_BADGE = 'Today is PICK!';

/** 캐러셀이 다음 장으로 넘어가는 간격 (ms) */
export const CAROUSEL_INTERVAL_MS = 4000;

/**
 * 실제 지급액.
 *
 * ★ **서버의 지급 계산과 글자 그대로 같은 식이어야 한다.**
 *   `ControllerApiCampaign` : `$campaign->available ? reward * multiple : reward`
 *
 *   배수를 조건 없이 곱하고 있었다. `available` 은 '재생해도 되는가' 가 아니라
 *   **'배수가 붙는가'** 인데(`ModuleCampaign::availableBenefit()`), 기본
 *   리워드(`F_REWARD_TYPE='D'`)는 항상 false 다. 그래서 배수 2 짜리 기본 미션이
 *   목록에는 100P, 재생 화면에는 50P 로 나왔다 — 실제 지급액은 50P 다.
 *
 *   재생 화면도 이 함수를 쓴다. 두 곳에 같은 식을 따로 두면 또 어긋난다.
 */
export function missionReward(campaign: DaoCampaign): number {
  const reward = campaign.reward ?? 0;

  return campaign.available && campaign.rewardMultiple
    ? Math.round(reward * campaign.rewardMultiple)
    : reward;
}

/**
 * 배수가 **지금 실제로** 붙는가.
 *
 * `available` 은 '재생해도 되는가' 가 아니라 '배수가 붙는가' 다
 * (`ModuleCampaign::availableBenefit()`). 관리 화면에 배수를 적어 두어도
 * 리워드 방식이 '기본' 이거나 기간 밖이면 붙지 않는다.
 */
export function missionBoosted(campaign: DaoCampaign): boolean {
  return !!campaign.available && (campaign.rewardMultiple ?? 1) > 1;
}

/**
 * 베네핏 마감일을 '9월 30일' 로. 없으면 빈 문자열.
 *
 * 서버가 `YYYYMMDD` 문자열을 그대로 준다(`MC_CAMPAIGN` 의 날짜 컬럼이 VARCHAR2(8)).
 */
export function benefitUntil(campaign: DaoCampaign): string {
  const raw = (campaign.benefitEnddate ?? '').trim();
  if (!/^\d{8}$/.test(raw)) return '';

  return `${Number(raw.slice(4, 6))}월 ${Number(raw.slice(6, 8))}일`;
}

/** 미션이 이 탭에 속하는지 */
export function inFilter(campaign: DaoCampaign, filter: MissionFilter): boolean {
  return filter.campaignTypes.includes(campaign.type ?? '');
}

/**
 * 맨 위 'Today is PICK!' 에 올릴 미션들.
 *
 * 관리 화면에서 여러 건을 고를 수 있고(`F_PICK`), 고른 순서(`F_PICK_ORDER`)대로
 * 캐러셀이 돈다. **하나도 고르지 않았으면 목록 첫 건을 쓴다** — 시안에 빈 자리가
 * 없어서, 운영자가 깜빡해도 화면이 비지 않게 한다.
 */
export function todayPick(campaigns: DaoCampaign[]): DaoCampaign | undefined {
  // ★ **한 건만** (2026-09-15 요청 — 슬라이드 삭제). 관리 화면이 저장할 때 나머지
  //   PICK 을 내리지만, 그 전에 여러 건이 걸려 있어도 화면은 첫 건만 쓴다.
  //   목록이 최신순이라 가장 최근 미션이 먼저 잡힌다.
  return campaigns.find((campaign) => campaign.pick === 'Y') ?? campaigns[0];
}
